import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Dialog,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import i18n from '../../i18n';
import config from '../../config';
import { useStateSelector } from '../../context/StateSelectors';
import {
    filterMenuByPermissions,
    flattenMenuLeaves
} from '../../utils/menuPermissions';
import { fuzzyFilter } from './fuzzyMatch';

// With thirty-odd grids, finding a page means either remembering which group it
// hides under or scrolling the sidebar. This is the shortcut: type two or three
// letters, press Enter.

const CommandPalette = ({ open, onClose }) => {
    const navigate = useNavigate();
    // Not defaulted to {} here: a fresh object literal every render would make
    // the memo below recompute on every keystroke.
    const permissions = useStateSelector((state) => state.permissions);

    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef(null);

    // Same filtering as the sidebar, so the palette can never reach a page the
    // sidebar hides.
    const entries = useMemo(() => {
        const allowed = filterMenuByPermissions(config.menu, permissions || {});
        return flattenMenuLeaves(allowed).map((leaf) => {
            const label = i18n.t(leaf.title);
            const context = leaf.trail.map((key) => i18n.t(key));
            return {
                ...leaf,
                label,
                context,
                // Path is included so "/app/users" finds the page too.
                searchText: [...context, label, leaf.path].join(' ')
            };
        });
    }, [permissions]);

    const results = useMemo(() => fuzzyFilter(entries, query.trim()), [entries, query]);

    // Reset per opening rather than per keystroke, so reopening starts clean.
    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIndex(0);
        }
    }, [open]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    // Keep the highlighted row in view when arrowing past the fold.
    useEffect(() => {
        const node = listRef.current?.children?.[activeIndex];
        node?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const go = useCallback(
        (entry) => {
            if (!entry) return;
            onClose();
            navigate(entry.path);
        },
        [navigate, onClose]
    );

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => (results.length ? (i + 1) % results.length : 0));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) =>
                results.length ? (i - 1 + results.length) % results.length : 0
            );
        } else if (event.key === 'Enter') {
            event.preventDefault();
            go(results[activeIndex]);
        }
        // Escape is handled by Dialog's own onClose.
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            // Sits high on the screen: the results grow downward, so anchoring
            // the input near the top keeps it from moving as you type.
            sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
            PaperProps={{ sx: { mt: '12vh' } }}
        >
            <Box sx={{ p: 2, pb: 1 }}>
                <OutlinedInput
                    autoFocus
                    fullWidth
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={i18n.t('common.searchPages')}
                    startAdornment={
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    }
                />
            </Box>

            {results.length ? (
                <List ref={listRef} sx={{ maxHeight: '50vh', overflowY: 'auto', pt: 0 }}>
                    {results.map((entry, index) => (
                        <ListItemButton
                            key={entry.path}
                            selected={index === activeIndex}
                            // Two lines' height whether or not the page sits
                            // in a section, so Dashboard lines up with the rest.
                            sx={{ minHeight: 60 }}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => go(entry)}
                        >
                            {entry.icon && (
                                <ListItemIcon sx={{ color: 'text.secondary' }}>
                                    {entry.icon}
                                </ListItemIcon>
                            )}
                            <ListItemText
                                primary={entry.label}
                                secondary={
                                    entry.context.length
                                        ? entry.context.join(' / ')
                                        : null
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>
            ) : (
                <Box sx={{ px: 3, pt: 2, pb: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('common.noPagesFound')}
                    </Typography>
                </Box>
            )}
        </Dialog>
    );
};

CommandPalette.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default CommandPalette;
